import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, ExternalLink, RefreshCw, Share2, Bookmark, BookmarkCheck, Heart, MessageSquare, Copy, X, AlertCircle, CheckCircle, Clock, Search, Image, Video, FileText, Music, Trash2 } from 'lucide-react';

interface SmartLink {
  id: string;
  url: string;
  title: string;
  description: string;
  imageUrl?: string;
  domain: string;
  type: 'article' | 'video' | 'image' | 'audio' | 'document' | 'social' | 'other';
  favicon?: string;
  author?: string;
  publishedAt?: Date;
  readTime?: number;
  tags: string[];
  isBookmarked: boolean;
  isLiked: boolean;
  likes: number;
  views: number;
  isVerified: boolean;
  isSafe: boolean;
  previewData?: {
    title: string;
    description: string;
    image: string;
    siteName: string;
    url: string;
  };
  position: {
    startOffset: number;
    endOffset: number;
    xpath: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

interface SmartLinksProps {
  contentRef: React.RefObject<HTMLElement | null>;
  onLinkCreate?: (link: SmartLink) => void;
  onLinkUpdate?: (link: SmartLink) => void;
  onLinkDelete?: (linkId: string) => void;
  onLinkShare?: (link: SmartLink) => void;
  className?: string;
}

const SmartLinks: React.FC<SmartLinksProps> = ({
  contentRef,
  onLinkCreate,
  onLinkUpdate,
  onLinkDelete,
  onLinkShare,
  className = ''
}) => {
  const [links, setLinks] = useState<SmartLink[]>([]);
  const [showLinks, setShowLinks] = useState(true);
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'type' | 'likes' | 'views'>('date');
  const [showSettings, setShowSettings] = useState(false);

  // Mock data
  useEffect(() => {
    const mockLinks: SmartLink[] = [
      {
        id: '1',
        url: 'https://react.dev/blog/2023/03/22/react-labs-what-we-have-been-working-on-march-2023',
        title: 'React Labs: What We Have Been Working On – March 2023',
        description: 'Updates on React Server Components, Suspense, and other experimental features',
        imageUrl: 'https://react.dev/blog/2023/03/22/react-labs-what-we-have-been-working-on-march-2023/cover.png',
        domain: 'react.dev',
        type: 'article',
        favicon: 'https://react.dev/favicon.ico',
        author: 'React Team',
        publishedAt: new Date('2023-03-22'),
        readTime: 8,
        tags: ['react', 'server-components', 'suspense', 'web-development'],
        isBookmarked: true,
        isLiked: false,
        likes: 42,
        views: 1250,
        isVerified: true,
        isSafe: true,
        previewData: {
          title: 'React Labs: What We Have Been Working On – March 2023',
          description: 'Updates on React Server Components, Suspense, and other experimental features',
          image: 'https://react.dev/blog/2023/03/22/react-labs-what-we-have-been-working-on-march-2023/cover.png',
          siteName: 'React',
          url: 'https://react.dev/blog/2023/03/22/react-labs-what-we-have-been-working-on-march-2023'
        },
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: ''
        },
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '2',
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: 'Never Gonna Give You Up - Rick Astley',
        description: 'The official video for "Never Gonna Give You Up" by Rick Astley',
        imageUrl: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
        domain: 'youtube.com',
        type: 'video',
        favicon: 'https://www.youtube.com/favicon.ico',
        author: 'Rick Astley',
        publishedAt: new Date('2009-10-25'),
        readTime: 3,
        tags: ['music', 'video', 'rick-astley', 'classic'],
        isBookmarked: false,
        isLiked: true,
        likes: 1500000,
        views: 1000000000,
        isVerified: true,
        isSafe: true,
        previewData: {
          title: 'Never Gonna Give You Up - Rick Astley',
          description: 'The official video for "Never Gonna Give You Up" by Rick Astley',
          image: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
          siteName: 'YouTube',
          url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'
        },
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: ''
        },
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];

    setLinks(mockLinks);
  }, []);

  // Auto-detect links in content
  const detectLinks = useCallback(() => {
    if (!contentRef.current) return;

    const text = contentRef.current.textContent || '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = text.match(urlRegex) || [];

    matches.forEach(url => {
      if (!links.find(link => link.url === url)) {
        generateLinkPreview(url);
      }
    });
  }, [contentRef, links]);

  // Generate link preview
  const generateLinkPreview = async (url: string) => {
    setIsGenerating(true);
    
    try {
      // Simulate API call to generate preview
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const domain = new URL(url).hostname;
      const link: SmartLink = {
        id: Date.now().toString(),
        url,
        title: `Link to ${domain}`,
        description: `External link to ${domain}`,
        domain,
        type: 'other',
        tags: [],
        isBookmarked: false,
        isLiked: false,
        likes: 0,
        views: 0,
        isVerified: false,
        isSafe: true,
        position: {
          startOffset: 0,
          endOffset: 0,
          xpath: ''
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setLinks(prev => [...prev, link]);
      onLinkCreate?.(link);
    } catch (error) {
      console.error('Error generating link preview:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Update link
  const updateLink = (linkId: string, updates: Partial<SmartLink>) => {
    setLinks(prev => prev.map(link => 
      link.id === linkId 
        ? { ...link, ...updates, updatedAt: new Date() }
        : link
    ));
    
    const updatedLink = links.find(l => l.id === linkId);
    if (updatedLink) {
      onLinkUpdate?.({ ...updatedLink, ...updates });
    }
  };

  // Delete link
  const deleteLink = (linkId: string) => {
    setLinks(prev => prev.filter(link => link.id !== linkId));
    onLinkDelete?.(linkId);
  };

  // Share link
  const shareLink = (link: SmartLink) => {
    onLinkShare?.(link);
  };

  // Get link type icon
  const getLinkTypeIcon = (type: string) => {
    switch (type) {
      case 'article': return <FileText className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'audio': return <Music className="w-4 h-4" />;
      case 'document': return <FileText className="w-4 h-4" />;
      case 'social': return <MessageSquare className="w-4 h-4" />;
      default: return <ExternalLink className="w-4 h-4" />;
    }
  };

  // Get link type color
  const getLinkTypeColor = (type: string) => {
    switch (type) {
      case 'article': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'video': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      case 'image': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'audio': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      case 'document': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-400';
      case 'social': return 'text-pink-600 bg-pink-100 dark:bg-pink-900 dark:text-pink-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // Filtered and sorted links
  const filteredLinks = links
    .filter(link => {
      const matchesType = filterType === 'all' || link.type === filterType;
      const matchesSearch = searchQuery === '' || 
        link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        link.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        link.domain.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'type':
          return a.type.localeCompare(b.type);
        case 'likes':
          return b.likes - a.likes;
        case 'views':
          return b.views - a.views;
        default:
          return 0;
      }
    });

  // Auto-detect links on mount
  useEffect(() => {
    detectLinks();
  }, [detectLinks]);

  return (
    <div className={`smart-links ${className}`}>
      {/* Links Panel */}
      {showLinks && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Link className="w-5 h-5 text-blue-500" />
                Smart Links
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={detectLinks}
                  disabled={isGenerating}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {isGenerating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setShowLinks(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Filters */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search links..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="article">Articles</option>
                  <option value="video">Videos</option>
                  <option value="image">Images</option>
                  <option value="audio">Audio</option>
                  <option value="document">Documents</option>
                  <option value="social">Social</option>
                  <option value="other">Other</option>
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="date">Date</option>
                  <option value="type">Type</option>
                  <option value="likes">Likes</option>
                  <option value="views">Views</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3">
              {filteredLinks.map(link => (
                <div
                  key={link.id}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => setShowPreview(showPreview === link.id ? null : link.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {link.favicon ? (
                        <img
                          src={link.favicon}
                          alt={link.domain}
                          className="w-4 h-4"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className={`w-4 h-4 rounded ${getLinkTypeColor(link.type)} flex items-center justify-center`}>
                          {getLinkTypeIcon(link.type)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {link.title}
                        </h4>
                        <div className="flex items-center gap-1">
                          {link.isVerified && (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          )}
                          {!link.isSafe && (
                            <AlertCircle className="w-3 h-3 text-red-500" />
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {link.description}
                      </p>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="truncate">{link.domain}</span>
                        <span className={`px-2 py-1 rounded-full ${getLinkTypeColor(link.type)}`}>
                          {link.type}
                        </span>
                        {link.readTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {link.readTime}m
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLink(link.id, { isBookmarked: !link.isBookmarked });
                          }}
                          className={`p-1 rounded transition-colors ${
                            link.isBookmarked 
                              ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20' 
                              : 'text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {link.isBookmarked ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateLink(link.id, { isLiked: !link.isLiked, likes: link.isLiked ? link.likes - 1 : link.likes + 1 });
                          }}
                          className={`p-1 rounded transition-colors ${
                            link.isLiked 
                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
                              : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Heart className={`w-3 h-3 ${link.isLiked ? 'fill-current' : ''}`} />
                        </button>
                        
                        <span className="text-xs text-gray-500">{link.likes}</span>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(link.url);
                          }}
                          className="p-1 text-gray-400 hover:text-green-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            shareLink(link);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Share2 className="w-3 h-3" />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLink(link.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Preview */}
                  {showPreview === link.id && link.previewData && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-start gap-3">
                        {link.previewData.image && (
                          <img
                            src={link.previewData.image}
                            alt={link.previewData.title}
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                            {link.previewData.title}
                          </h5>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                            {link.previewData.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{link.previewData.siteName}</span>
                            <span>•</span>
                            <span>{link.domain}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {filteredLinks.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Link className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No links found</p>
                  <p className="text-sm">Links will be auto-detected in content</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartLinks;
